import { useFormContext } from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { LabelText, TitleText } from '@calendly/ui/components/typography'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  PaymentAmount,
  PaymentCoupons,
  PaymentStatusBanner,
  PaymentTerms,
  buildProviderOptions,
  computePaymentsEnabledState,
  getPaypalFormRowsVisibility,
  useCreateBookingCodeMutation,
  useDeleteBookingCodeMutation,
  useGetBookingCodesQuery,
  useUpdateBookingCodeMutation,
} from 'features/event_type_editor'
import { useEventType } from 'features/event_type_provider'

import { PaymentProcessorSelect } from './payment_processor_select'
import {
  dividerCss,
  enabledFormCss,
  priceFieldsCss,
  priceHeaderCss,
  priceSectionCss,
  processorLabelCss,
  processorSelectCss,
  sectionBlockCss,
} from './styles'

export const PaymentsEnabledForm = ({
  provider,
  paymentMethodEnabled,
  isUpgradeRequired,
  isStripeConnected,
  isPaypalConnected,
  isPaypalPpcpConnected,
}) => {
  const { setFieldValue } = useFormContext()
  const paymentCouponsRef = useRef(null)
  const eventType = useEventType()
  const { isMETParent } = eventType

  const [stripeEnabled, setStripeEnabled] = useState(false)
  const [paypalLegacyEnabled, setPaypalLegacyEnabled] = useState(false)
  const [paypalPpcpEnabled, setPaypalPpcpEnabled] = useState(false)

  const { data: couponsData, isLoading: isLoadingCoupons, error: couponsError } = useGetBookingCodesQuery({
    eventTypeUuid: eventType?.uuid,
    count: 100,
  }, {
    skip: !eventType?.uuid,
  })

  const filteredCoupons = useMemo(() => {
    if (!couponsData?.collection) return []
    return couponsData.collection
  }, [couponsData?.collection])

  const [createBookingCode, { isLoading: isCreating }] = useCreateBookingCodeMutation()
  const [deleteBookingCode, { isLoading: isDeleting }] = useDeleteBookingCodeMutation()
  const [updateBookingCode, { isLoading: isUpdating }] = useUpdateBookingCodeMutation()

  const isStripeDisconnected = paymentMethodEnabled && provider === 'stripe' && !isStripeConnected
  const isPaypalLegacyDisconnected = paymentMethodEnabled && provider === 'paypal' && !isPaypalConnected
  const isPaypalPpcpDisconnected = paymentMethodEnabled && provider === 'paypal_ppcp' && !isPaypalPpcpConnected
  const isPaypalDisconnected = isPaypalLegacyDisconnected || isPaypalPpcpDisconnected

  const paypalRows = useMemo(() => getPaypalFormRowsVisibility({
    isPaypalConnected,
    isPaypalPpcpConnected,
    provider,
  }), [isPaypalConnected, isPaypalPpcpConnected, provider])

  useEffect(() => {
    const ui = computePaymentsEnabledState({
      isPaymentsIntegrationAvailable: !isUpgradeRequired,
      isPaypalConnected,
      isPaypalPpcpConnected,
      isPaypalDisconnected,
      isStripeConnected,
      isStripeDisconnected,
      paymentMethodEnabled,
      provider,
    })

    setStripeEnabled(ui.isStripeEnabled)
    setPaypalLegacyEnabled(ui.isPaypalLegacyEnabled)
    setPaypalPpcpEnabled(ui.isPaypalPpcpEnabled)
  }, [
    isUpgradeRequired,
    isPaypalConnected,
    isPaypalPpcpConnected,
    isPaypalDisconnected,
    isStripeConnected,
    isStripeDisconnected,
    paymentMethodEnabled,
    provider,
  ])

  const providerOptions = buildProviderOptions({
    isStripeEnabled: stripeEnabled,
    isPaypalLegacyEnabled: paypalLegacyEnabled,
    isPaypalPpcpEnabled: paypalPpcpEnabled,
    paypalRows,
  })

  const handleProviderSelection = (selectedProvider) => {
    setFieldValue('provider', selectedProvider)
  }

  const handleCurrencyChange = () => {
    if (paymentCouponsRef.current) {
      paymentCouponsRef.current.closeModal()
    }
  }

  return (
    <div className={enabledFormCss} data-testid="payments-enabled-form">
      <section className={priceSectionCss}>
        <div className={priceHeaderCss}>
          <TitleText level={3} size="medium">
            {I18n.t('publisher.event_type_editor_crud.payments.price_title')}
          </TitleText>
          <div className={processorSelectCss}>
            <LabelText color="muted" className={processorLabelCss}>
              {I18n.t('publisher.event_type_editor_crud.payments.payment_processor_label')}
            </LabelText>
            <PaymentProcessorSelect
              aria-label={I18n.t('publisher.event_types.payment_method.payment_processor_label')}
              value={provider}
              onChange={handleProviderSelection}
              options={providerOptions}
            />
          </div>
        </div>

        <PaymentStatusBanner
          provider={provider}
          isStripeConnected={isStripeConnected}
          isPaypalConnected={isPaypalConnected}
          isPaypalPpcpConnected={isPaypalPpcpConnected}
        />

        <div className={priceFieldsCss}>
          <PaymentAmount
            label={I18n.t('publisher.event_type_editor_crud.payments.amount_label')}
            required
            requiredMark="asterisk"
            bold
            newForm
            coupons={filteredCoupons}
            onDeleteCoupon={deleteBookingCode}
            onCurrencyChange={handleCurrencyChange}
          />
        </div>
      </section>

      {provider === 'stripe' && !isMETParent && (
        <>
          <hr className={dividerCss} />
          <section className={sectionBlockCss}>
            <PaymentCoupons
              ref={paymentCouponsRef}
              variant="crud"
              coupons={filteredCoupons}
              isLoading={isLoadingCoupons}
              error={couponsError}
              onCreateCoupon={createBookingCode}
              onUpdateCoupon={updateBookingCode}
              onDeleteCoupon={deleteBookingCode}
              eventType={eventType}
              isCreating={isCreating}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
            />
          </section>
        </>
      )}

      <hr className={dividerCss} />
      <section className={sectionBlockCss}>
        <PaymentTerms variant="crud" />
      </section>
    </div>
  )
}
